
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { StrategyPerformance } from "@/types/strategy-types";

export const useAccountPerformances = (userRole: string | null, providerId: string | null) => {
  const { toast } = useToast();
  
  const fetchAccountPerformances = async (): Promise<StrategyPerformance[]> => {
    try {
      let query = supabase.from('account_performance').select('*');
      
      if (userRole === 'strategy_provider' && providerId) {
        const { data: strategyIds } = await supabase
          .from('strategy')
          .select('id')
          .eq('strategy_provider_id', providerId);
        
        if (strategyIds && strategyIds.length > 0) {
          const { data: accountIds } = await supabase
            .from('account')
            .select('id, strategy_id')
            .in('strategy_id', strategyIds.map(s => s.id));
          
          if (accountIds && accountIds.length > 0) {
            query = query.in('account_id', accountIds.map(a => a.id));
          } else {
            return [];
          }
        } else {
          return [];
        }
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Transform account_performance data to match StrategyPerformance interface
      return (data || []).map(perf => ({
        id: perf.id,
        strategy_id: perf.account_id, // Use account_id as strategy_id for compatibility
        account_id: perf.account_id,
        timestamp: perf.timestamp,
        win_rate: perf.win_rate,
        max_drawdown: perf.max_drawdown,
        pnl_ratio: perf.pnl_ratio,
        total_trades: perf.num_wins + perf.num_losses,
        num_followers: perf.num_followers,
        num_wins: perf.num_wins,
        num_losses: perf.num_losses
      }));
    } catch (error) {
      console.error("Error fetching account performances:", error);
      toast({
        title: "Error",
        description: "Failed to load account performance data",
        variant: "destructive",
      });
      return [];
    }
  };

  return {
    fetchAccountPerformances
  };
};
